import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Puts the *document* scroll position at the top on navigation.
 * - Uses `location.key` so every history entry (including same pathname, new query) resets scroll.
 * - Resets `scrollingElement` + legacy roots — not only `window` — for consistent browser behavior.
 * - No smooth behavior: that animates after paint and often loses to layout/hydration, so you end up partway down the page.
 * - Runs an extra frame so any route layout is painted before we correct scroll.
 */
function scrollDocumentToTop() {
  const root = document.scrollingElement;
  if (root) {
    root.scrollTop = 0;
  }
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  window.scrollTo(0, 0);
}

export function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useLayoutEffect(() => {
    scrollDocumentToTop();
    const raf = requestAnimationFrame(() => {
      scrollDocumentToTop();
    });
    return () => cancelAnimationFrame(raf);
  }, [location.key]);

  // After scrolling to top, honor in-page hash targets (e.g. /docs#section).
  useLayoutEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, "");
    if (!id) return;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ block: "start" });
    }
  }, [location.key, location.hash]);

  return null;
}
